/**
 * Tax identifiers of the registry subjects (proposal §2.3): Italian VAT number
 * and tax code with their check characters, EU VAT numbers by country format,
 * free identifiers outside the EU. Also the IBAN check.
 */
/** Countries offered in the registry, by ISO code; EU members carry their VAT prefix. */
export const COUNTRIES = [
    { code: 'IT', name: 'Italia', vatPrefix: 'IT' },
    { code: 'AT', name: 'Austria', vatPrefix: 'AT' },
    { code: 'BE', name: 'Belgio', vatPrefix: 'BE' },
    { code: 'BG', name: 'Bulgaria', vatPrefix: 'BG' },
    { code: 'CY', name: 'Cipro', vatPrefix: 'CY' },
    { code: 'HR', name: 'Croazia', vatPrefix: 'HR' },
    { code: 'DK', name: 'Danimarca', vatPrefix: 'DK' },
    { code: 'EE', name: 'Estonia', vatPrefix: 'EE' },
    { code: 'FI', name: 'Finlandia', vatPrefix: 'FI' },
    { code: 'FR', name: 'Francia', vatPrefix: 'FR' },
    { code: 'DE', name: 'Germania', vatPrefix: 'DE' },
    { code: 'GR', name: 'Grecia', vatPrefix: 'EL' },
    { code: 'IE', name: 'Irlanda', vatPrefix: 'IE' },
    { code: 'LV', name: 'Lettonia', vatPrefix: 'LV' },
    { code: 'LT', name: 'Lituania', vatPrefix: 'LT' },
    { code: 'LU', name: 'Lussemburgo', vatPrefix: 'LU' },
    { code: 'MT', name: 'Malta', vatPrefix: 'MT' },
    { code: 'NL', name: 'Paesi Bassi', vatPrefix: 'NL' },
    { code: 'PL', name: 'Polonia', vatPrefix: 'PL' },
    { code: 'PT', name: 'Portogallo', vatPrefix: 'PT' },
    { code: 'CZ', name: 'Repubblica Ceca', vatPrefix: 'CZ' },
    { code: 'RO', name: 'Romania', vatPrefix: 'RO' },
    { code: 'SK', name: 'Slovacchia', vatPrefix: 'SK' },
    { code: 'SI', name: 'Slovenia', vatPrefix: 'SI' },
    { code: 'ES', name: 'Spagna', vatPrefix: 'ES' },
    { code: 'SE', name: 'Svezia', vatPrefix: 'SE' },
    { code: 'HU', name: 'Ungheria', vatPrefix: 'HU' },
    { code: 'CH', name: 'Svizzera' },
    { code: 'CN', name: 'Cina' },
    { code: 'NO', name: 'Norvegia' },
    { code: 'GB', name: 'Regno Unito' },
    { code: 'SM', name: 'San Marino' },
    { code: 'US', name: 'Stati Uniti' },
    { code: 'XX', name: 'Altro paese' },
];
const EU_VAT_FORMATS = {
    AT: /^ATU\d{8}$/,
    BE: /^BE[01]\d{9}$/,
    BG: /^BG\d{9,10}$/,
    CY: /^CY\d{8}[A-Z]$/,
    CZ: /^CZ\d{8,10}$/,
    DE: /^DE\d{9}$/,
    DK: /^DK\d{8}$/,
    EE: /^EE\d{9}$/,
    EL: /^EL\d{9}$/,
    ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
    FI: /^FI\d{8}$/,
    FR: /^FR[A-HJ-NP-Z0-9]{2}\d{9}$/,
    HR: /^HR\d{11}$/,
    HU: /^HU\d{8}$/,
    IE: /^IE(\d{7}[A-W][A-I]?|\d[A-Z+*]\d{5}[A-W])$/,
    LT: /^LT(\d{9}|\d{12})$/,
    LU: /^LU\d{8}$/,
    LV: /^LV\d{11}$/,
    MT: /^MT\d{8}$/,
    NL: /^NL\d{9}B\d{2}$/,
    PL: /^PL\d{10}$/,
    PT: /^PT\d{9}$/,
    RO: /^RO\d{2,10}$/,
    SE: /^SE\d{12}$/,
    SI: /^SI\d{8}$/,
    SK: /^SK\d{10}$/,
};
/** Upper case, without spaces, dots, dashes and slashes. */
export function normaliseTaxId(value) {
    return String(value)
        .toUpperCase()
        .replace(/[\s.\-/]/g, '');
}
/** A tax identifier compared without the Italian prefix, spaces or case. */
export function taxIdKey(value) {
    if (!value)
        return null;
    const key = normaliseTaxId(value).replace(/^IT(?=\d{11}$)/, '');
    return key || null;
}
/**
 * The tax code that names the company apart from its VAT number: in a Gruppo
 * IVA each company has its own. Equal to the VAT number (also when copied from
 * it at entry) it tells nothing more than the VAT number.
 */
function ownTaxCode(ids) {
    const code = taxIdKey(ids.taxCode);
    return code && code !== taxIdKey(ids.vatNumber) ? code : null;
}
/**
 * Whether two pairs of identifiers are the same subject (2.31). In a Gruppo
 * IVA the companies share the VAT number and keep their own tax code: when
 * both have one, it decides; otherwise any identifier in common is enough, as
 * before.
 */
export function sameTaxSubject(a, b) {
    const aOwn = ownTaxCode(a);
    const bOwn = ownTaxCode(b);
    if (aOwn && bOwn)
        return aOwn === bOwn;
    const aKeys = [taxIdKey(a.vatNumber), taxIdKey(a.taxCode)].filter(Boolean);
    return [taxIdKey(b.vatNumber), taxIdKey(b.taxCode)].some((key) => key !== null && aKeys.includes(key));
}
/**
 * The subject the identifiers of a document belong to (2.31): the one with
 * its tax code, else the only one with its VAT number whose own tax code does
 * not name another company. A document of a Gruppo IVA with a tax code nobody
 * has belongs to no one yet: a new company of the group.
 */
export function subjectByTaxIds(subjects, ids) {
    const vat = taxIdKey(ids.vatNumber);
    const code = taxIdKey(ids.taxCode);
    if (code) {
        const byCode = subjects.find((item) => taxIdKey(item.taxCode) === code);
        if (byCode)
            return byCode;
    }
    if (!vat)
        return null;
    const own = ownTaxCode(ids);
    const byVat = subjects.filter((item) => {
        const itemVat = taxIdKey(item.vatNumber);
        if (itemVat !== vat && !(itemVat === null && taxIdKey(item.taxCode) === vat))
            return false;
        const itemOwn = ownTaxCode(item);
        return !own || !itemOwn || itemOwn === own;
    });
    return byVat.length === 1 ? byVat[0] : null;
}
/** Italian VAT number (11 digits) or numeric tax code of an entity: the check digit. */
function italianNumericError(value, what) {
    if (!/^\d{11}$/.test(value))
        return `${what}: servono 11 cifre`;
    let sum = 0;
    for (let i = 0; i < 10; i++) {
        let digit = Number(value[i]);
        if (i % 2 === 1) {
            digit *= 2;
            if (digit > 9)
                digit -= 9;
        }
        sum += digit;
    }
    return (10 - (sum % 10)) % 10 === Number(value[10])
        ? null
        : `${what}: la cifra di controllo non corrisponde, controlla di averla scritta bene`;
}
const ODD_VALUES = {
    0: 1,
    1: 0,
    2: 5,
    3: 7,
    4: 9,
    5: 13,
    6: 15,
    7: 17,
    8: 19,
    9: 21,
    A: 1,
    B: 0,
    C: 5,
    D: 7,
    E: 9,
    F: 13,
    G: 15,
    H: 17,
    I: 19,
    J: 21,
    K: 2,
    L: 4,
    M: 18,
    N: 20,
    O: 11,
    P: 3,
    Q: 6,
    R: 8,
    S: 12,
    T: 14,
    U: 16,
    V: 10,
    W: 22,
    X: 25,
    Y: 24,
    Z: 23,
};
export function italianVatError(vat) {
    return italianNumericError(vat, 'Partita IVA');
}
/** Personal tax code (16 characters) or numeric one of companies and entities (11 digits). */
export function italianTaxCodeError(code) {
    if (/^\d{11}$/.test(code))
        return italianNumericError(code, 'Codice fiscale');
    if (!/^[A-Z]{6}[0-9LMNPQRSTUV]{2}[A-EHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/.test(code)) {
        return 'Codice fiscale: 16 caratteri (o 11 cifre per società ed enti)';
    }
    let sum = 0;
    for (let i = 0; i < 15; i++) {
        const char = code[i];
        sum +=
            i % 2 === 0 ? ODD_VALUES[char] : /\d/.test(char) ? Number(char) : char.charCodeAt(0) - 65;
    }
    return String.fromCharCode(65 + (sum % 26)) === code[15]
        ? null
        : 'Codice fiscale: il carattere di controllo non corrisponde';
}
/**
 * Checks and normalises the identifiers of a subject: Italian VAT without the
 * «IT» prefix, EU VAT with the country prefix, the tax code of a company
 * proposed equal to its VAT number. Errors by field.
 */
export function checkTaxIds(input) {
    let vat = input.vatNumber ? normaliseTaxId(input.vatNumber) : '';
    let tax = input.taxCode ? normaliseTaxId(input.taxCode) : '';
    const errors = {};
    const warnings = [];
    const country = COUNTRIES.find((item) => item.code === input.country);
    // With `allowMissing` the subject is entered now and completed later: what is
    // written is still checked, what is missing is not asked for.
    const required = !input.allowMissing;
    if (input.country === 'IT') {
        if (vat.startsWith('IT'))
            vat = vat.slice(2);
        if (vat)
            errors.vatNumber = italianVatError(vat) ?? undefined;
        if (tax)
            errors.taxCode = italianTaxCodeError(tax) ?? undefined;
        if (required && input.requirement === 'P.IVA' && !vat)
            errors.vatNumber = 'Partita IVA obbligatoria per questa tipologia';
        if (required && input.requirement === 'Codice fiscale' && !tax)
            errors.taxCode = 'Codice fiscale obbligatorio per questa tipologia';
        if (required && input.requirement === 'P.IVA o codice fiscale' && !vat && !tax) {
            errors.vatNumber = 'Serve la partita IVA o il codice fiscale';
        }
        if (vat && !tax && !errors.vatNumber && input.requirement !== 'Codice fiscale')
            tax = vat;
    }
    else if (country?.vatPrefix) {
        if (vat && !vat.startsWith(country.vatPrefix))
            vat = country.vatPrefix + vat;
        if (!vat) {
            if (required)
                errors.vatNumber = 'Partita IVA comunitaria obbligatoria';
        }
        else if (!EU_VAT_FORMATS[country.vatPrefix].test(vat)) {
            const message = `Partita IVA: formato non valido per ${country.name}`;
            if (input.lenient)
                warnings.push(message);
            else
                errors.vatNumber = message;
        }
    }
    else {
        if (required && !vat && !tax)
            errors.vatNumber = 'Serve un identificativo fiscale del paese';
        if (vat && vat.length < 4)
            errors.vatNumber = 'Identificativo troppo corto';
    }
    for (const key of ['vatNumber', 'taxCode'])
        if (!errors[key])
            delete errors[key];
    return { vatNumber: vat || null, taxCode: tax || null, errors, warnings };
}
/** IBAN: country, length and the mod-97 check. */
export function ibanError(value) {
    const iban = value.toUpperCase().replace(/\s/g, '');
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban))
        return 'IBAN non valido';
    if (iban.startsWith('IT') && iban.length !== 27)
        return 'IBAN italiano: 27 caratteri';
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let remainder = 0;
    for (const char of rearranged) {
        const digits = /\d/.test(char) ? char : String(char.charCodeAt(0) - 55);
        for (const digit of digits)
            remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1 ? null : 'IBAN: i caratteri di controllo non corrispondono';
}
export function normaliseIban(value) {
    return value.toUpperCase().replace(/\s/g, '');
}
/** Name compared for duplicates: no case, accents, double spaces or invisible characters. */
export function nameKey(name) {
    return name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u200b-\u200d\ufeff]/g, '')
        .replace(/[.,'’]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}
//# sourceMappingURL=tax-ids.js.map